import { environment } from './environments/environment';
import { UserPreferencesService } from './shared/services/user-preferences/user-preferences.service';
import { PermissionService } from './core/services/permission.service';
import { authService } from './shared/services/auth/auth.service';
import { isWorkosAuth, resolveAuthProvider } from './core/config/auth-provider';

/**
 * The capability-token journeys — the pages a customer reaches from a QR code or an emailed link.
 *
 * Every route in `app-routing.module.ts` that is not behind `authGuard`, minus `/auth` (see
 * below). They authorise themselves with the token in the link (`x-booking-token`) and read
 * nothing the session bootstrap produces — no menu, no permissions, no company context — so
 * awaiting it before the router even starts buys them nothing.
 *
 * It is not free either. For an anonymous customer both steps return at their first storage check,
 * but for a signed-in staff member previewing the journey they are two serial round trips
 * (`MobileAPI/GetUserCompanyDetailsV1/{userId}` then `/api/me`, plus a preferences write when the
 * subdomain moved the company) in front of the first paint — and `/api/me` retries twice with
 * backoff before giving up, so a blip there holds the splash for another ~1.6 s.
 *
 * `/auth` is deliberately NOT in this list. It is unauthenticated too, but `companyContextInitializer`
 * has always run there and is what force-signs-out a session that cannot be recovered before the
 * login form renders; only `permissionInitializer` ever skipped it, and it still does. Whether the
 * login page should keep paying for that check is a separate question from this one — folding it in
 * here would have changed the login flow silently.
 */
const PUBLIC_PATH_PREFIXES: readonly string[] = [
  '/eoi/register',
  '/appointments',
  '/scheduler/bookslot',
  '/scheduler/editbookingslot',
  '/scheduler/reschedulebookingslot',
  '/scheduler/cancelbookingslot',
];

/**
 * Whether a path is one of the capability-token journeys above.
 *
 * Compared lower-cased because these routes are registered in PascalCase (`/scheduler/BookSlot`)
 * and the links in the wild are not consistent about it. Prefix matching is segment-aware, so
 * `/eoi/register/address` matches and a future `/appointments-admin` would not.
 *
 * False for `/auth`, which is unauthenticated but not token-gated — see PUBLIC_PATH_PREFIXES.
 */
export function isPublicPath(pathname: string = window.location.pathname): boolean {
  const path = pathname.toLowerCase();
  return PUBLIC_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function permissionInitializer(permissionService: PermissionService, auth: authService) {
  return async () => {
    // Skip on auth/public pages
    if (window.location.pathname.startsWith('/auth') || isPublicPath()) return;

    // WorkOS (COL-786): the token lives in memory only, so after a reload it is re-fetched from the session cookie.
    if (isWorkosAuth()) await auth.checkTokenAndRefresh();
    const tokens = auth.getTokenFromStorage();
    if (!tokens?.refresh_token) return;

    if (!tokens.access_token || auth.isTokenExpired(tokens.access_token)) {
      const refreshed = await auth.checkTokenAndRefresh();
      if (!refreshed) return;
    }

    await permissionService.load();
  };
}

export function companyContextInitializer(userPreferencesService: UserPreferencesService, auth: authService) {
  return async () => {
    // WorkOS (COL-786): the token lives in memory only, so after a reload it is re-fetched from the session cookie.
    if (isWorkosAuth()) await auth.checkTokenAndRefresh();
    const tokens = auth.getTokenFromStorage();

    // No tokens at all — auth guard handles redirect to /auth/login.
    // Do NOT call signOut() here: this is the normal state on the post-logout
    // Cognito callback. Calling signOut() would cause a redirect loop.
    if (!tokens) return;

    // Step 1: session entry exists but refresh token absent → can't recover
    if (!tokens.refresh_token) {
      auth.signOut();
      return;
    }

    // Step 2 → 3: valid access token proceeds immediately; expired/absent triggers
    // a silent Cognito refresh — signOut() if Cognito rejects the refresh token
    if (!tokens.access_token || auth.isTokenExpired(tokens.access_token)) {
      const refreshed = await auth.checkTokenAndRefresh();
      if (!refreshed) {
        auth.signOut();
        return;
      }
    }

    let subdomain = window.location.hostname.match(/^[^.]+/)?.[0] || '';
    if (subdomain === 'localhost') subdomain = environment.defaultSubdomain;
    try {
      const result = await Promise.race([
        userPreferencesService.validateAndApplySubdomainPreferences(subdomain),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
      ]);
      if (!result.allowed) {
        auth.signOut();
      }
    } catch (err) {
      console.error('companyContextInitializer: subdomain validation failed, signing out', err);
      auth.signOut();
    }
  };
}

export function sessionInitializer(
  userPreferencesService: UserPreferencesService,
  permissionService: PermissionService,
  auth: authService
) {
  return async () => {
    // Nothing behind this is reachable from a capability-token page, and every millisecond of it
    // lands in front of the first paint. `/auth` is excluded — see PUBLIC_PATH_PREFIXES.
    if (isPublicPath()) return;

    // COL-786: which sign-in this company address uses (Cognito or WorkOS) decides everything below.
    await resolveAuthProvider();
    try {
      await companyContextInitializer(userPreferencesService, auth)();
    } catch (err) {
      console.error('sessionInitializer: company context step failed', err);
    }
    await permissionInitializer(permissionService, auth)();
  };
}
